import React, { MouseEventHandler } from "react";
import { Connection, Provider } from "../common/ObjectTypes";
import { useTranslation } from "react-i18next";
import { getConnectionStatus } from "./getConnectionStatus";
import { Switch } from "@headlessui/react";
import { AttentionIcon } from "../common/ProjectIcons";
import { Tooltip } from "../common/Tooltip";
import { containerClasses, connectionDetailsClasses } from "../common/tailwindClassnames";
import { HoverTooltip } from "../common/HoverTooltip";

type ConnectionDetailsProps = {
    reloadConnection: boolean,
    connection: Connection,
    connectionHasDestinations: boolean,
    connectionProvider: Provider,
    subPage: string | undefined,
    onSelectSubPage: Function,
    onChangeProviderEnabled: Function,
}

const ConnectionDetails = ({
    reloadConnection,
    connection,
    connectionHasDestinations,
    connectionProvider,
    subPage,
    onSelectSubPage,
    onChangeProviderEnabled
}: ConnectionDetailsProps) => {
    const { t } = useTranslation();
    const connectionStatus = getConnectionStatus(connection.status, true, t);

    return (
        <div className={connectionDetailsClasses.container}>
            <div className={connectionDetailsClasses.header}>
                <div className="max-w-list-table mx-auto">
                {/* page header */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                    <div className="flex flex-col md:w-3/4">
                        <div className="flex flex-row items-start"><h1 className={connectionDetailsClasses.title}>{connection.provider.name}</h1> {(!reloadConnection && connectionProvider.enabled) && <p className={`mx-3 mt-2 ${connectionStatus.style}`}>{connectionStatus.text}</p>}</div>
                        <div className="flex flex-row items-center gap-2 mt-2 mb-3">
                            {connection?.provider?.type?.name && <span className={connectionDetailsClasses.headerPill}>{connection.provider.type.name}</span>}
                            {connection?.owner?.name && <span className={connectionDetailsClasses.headerPill}>{connection.owner.name}</span>}
                        </div>
                    </div>
                    <div className={`mt-3 md:mt-0 !pl-0 md:!pl-4 ${connectionProvider.enabled ? connectionDetailsClasses.enabledPanel : connectionDetailsClasses.disabledPanel}`}>
                        <p className="text-left text-sm">
                            <span className={connectionDetailsClasses.switchText}>
                                {/* Both labels share one grid cell so the panel reserves the width of the
                                    wider one (Enabled vs Disabled, in any language) — toggling never resizes it. */}
                                <span className="grid">
                                    <span className={`${connectionDetailsClasses.switchTextLabel} col-start-1 row-start-1 ${connectionProvider.enabled ? "" : "invisible"}`} aria-hidden={!connectionProvider.enabled}>{t("Connection Enabled")}</span>
                                    <span className={`${connectionDetailsClasses.switchTextLabel} col-start-1 row-start-1 ${connectionProvider.enabled ? "invisible" : ""}`} aria-hidden={connectionProvider.enabled}>{t("Connection Disabled")}</span>
                                </span>
                                <span className="flex items-center translate-y-1">
                                    <Tooltip description={connectionProvider.enabled ? t("Sending data to its destinations") : t("Not sending data to its destinations")} position="bottom" />
                                </span>
                            </span>
                        </p>
                        <div className="w-16">
                            <Switch
                                checked={connectionProvider.enabled}
                                onChange={(e) => onChangeProviderEnabled(e)}
                                className={`
                                    ${connectionDetailsClasses.switchContainer}
                                    ${connectionProvider.enabled
                                        ? 'bg-status-green focus:ring-status-green/50'
                                        : 'bg-secondary-gray focus:ring-secondary-gray/50'
                                    } 
                                `}
                            >
                                <span className="sr-only">{connectionProvider.enabled ? t('Enabled') : t('Disabled')}</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="absolute right-2 top-1.5 h-4 w-4 text-white transition-opacity duration-200"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>

                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="absolute left-2 top-1.5 h-4 w-4 text-white transition-opacity duration-200"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>

                                {/* The moving button (without icons) */}
                                <span
                                    aria-hidden="true"
                                    className="pointer-events-none inline-block size-5 translate-x-0 rounded-full bg-white ring-0 shadow-lg transition duration-200 ease-in-out group-data-[checked]:translate-x-7"
                                />
                            </Switch>
                        </div>

                    </div>
                </div>
                {/* tabs for sub-pages */}
                <div className="w-full flex flex-row font-semibold -ml-3">
                    <label className={(subPage === "overview" ? connectionDetailsClasses.tabActive : connectionDetailsClasses.tabInactive).replace('mr-3', 'mx-3')}
                        onClick={() => {
                            onSelectSubPage("")
                        }}>
                        {t("General")}
                    </label>
                    <label className={(subPage === "provider" ? connectionDetailsClasses.tabActive : connectionDetailsClasses.tabInactive).replace('mr-3', 'mx-3')}
                        onClick={() => {
                            onSelectSubPage("provider")
                        }}>
                        {t("Provider")}
                    </label>
                    <label className={(subPage === "destinations" ? connectionDetailsClasses.tabActive : connectionDetailsClasses.tabInactive).replace('mr-3', 'mx-3')}
                        onClick={() => {
                            onSelectSubPage("destinations")
                        }}>
                        {connectionHasDestinations ? (
                            <span>{t("Destinations")}</span>
                        ) : (
                            <HoverTooltip position="bottom" width="w-56" content={t("This Connection needs at least one Destination")}>
                                <span className="flex flex-row items-baseline"><span className="mr-1.5 inline-flex transform -translate-y-0.5"><AttentionIcon /></span><span>{t("Destinations")}</span></span>
                            </HoverTooltip>
                        )}
                    </label>
                    <label className={(subPage === "sources" ? connectionDetailsClasses.tabActive : connectionDetailsClasses.tabInactive).replace('mr-3', 'mx-3')}
                        onClick={() => {
                            onSelectSubPage("sources")
                        }}>
                        {t("Sources")}
                    </label>
                    {(connectionProvider.webhook_configuration || connectionProvider.type?.webhook) && (
                        <label className={(subPage === "webhook" ? connectionDetailsClasses.tabActive : connectionDetailsClasses.tabInactive).replace('mr-3', 'mx-3')}
                            onClick={() => {
                                onSelectSubPage("webhook")
                            }}>
                            {t("Webhook")}
                        </label>
                    )}
                    {/* The Logs tab is disabled (with a tooltip) when the connection has no
                        destination, since logs require one. Gating is temporary, pending support
                        for logs on destination-less connections. */}
                    {connectionHasDestinations ? (
                        <label className={(subPage === "logs" ? connectionDetailsClasses.tabActive : connectionDetailsClasses.tabInactive).replace('mr-3', 'mx-3')}
                            onClick={() => {
                                onSelectSubPage("logs")
                            }}>
                            {t("Logs")}
                        </label>
                    ) : (
                        <label
                            className={connectionDetailsClasses.tabInactive.replace('mr-3', 'mx-3').replace('cursor-pointer', 'cursor-not-allowed')}
                            aria-disabled="true">
                            <HoverTooltip position="bottom" width="w-56" content={t("Assign a destination to complete this connection and view its logs.")}>
                                <span className="opacity-50">{t("Logs")}</span>
                            </HoverTooltip>
                        </label>
                    )}
                </div>
                </div>
            </div>
        </div>
    );
};

export default ConnectionDetails;